"use client"

import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import AssetComboIcon from "../DefuseSDK/components/Asset/AssetComboIcon"
import StatusFooter from "./StatusFooter"

type Mode = "send" | "receive"

const modes = {
  send: {
    label: "Send",
    states: ["Preparing transfer", "Sending", "Sent successfully"],
  },
  receive: {
    label: "Receive",
    states: ["Waiting for deposit", "Received 2500 USDC"],
  },
} as const

const PANEL_EASE = [0.22, 1, 0.36, 1] as const

const panelVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "40%" : "-40%",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-40%" : "40%",
    opacity: 0,
  }),
}

function SendPanel() {
  return (
    <>
      <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
        <AssetComboIcon
          icon="/static/icons/network/ethereum.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 text-sm/5">Token</div>
          <div className="font-semibold text-base/5 text-white tracking-tight">
            ETH
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
        <AssetComboIcon
          icon="/static/icons/network/base-light.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 text-sm/5">Network</div>
          <div className="font-semibold text-base/5 text-white tracking-tight">
            Base
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
        <AssetComboIcon
          icon="/static/icons/token/usdc.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 text-sm/5">Pay with</div>
          <div className="font-semibold text-base/5 text-white tracking-tight">
            Taler USDC
          </div>
        </div>
      </div>
    </>
  )
}

function ReceivePanel() {
  return (
    <>
      <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
        <AssetComboIcon
          icon="/static/icons/token/usdc.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 text-sm/5">Token</div>
          <div className="font-semibold text-base/5 text-white tracking-tight">
            USDC
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
        <AssetComboIcon
          icon="/static/icons/network/ethereum.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-medium text-gray-400 text-sm/5">Network</div>
          <div className="font-semibold text-base/5 text-white tracking-tight">
            Ethereum
          </div>
        </div>
      </div>
    </>
  )
}

const ReceiveSendIllustration = ({
  isActive = true,
}: {
  isActive?: boolean
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [mode, setMode] = useState<Mode>("send")
  const [direction, setDirection] = useState(0)
  const [animationKey, setAnimationKey] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    const element = rootRef.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.4 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isActive && isInView) {
      setHasStarted(true)
    }
  }, [isActive, isInView])

  const selectMode = (nextMode: Mode) => {
    if (nextMode === mode) {
      return
    }

    setDirection(nextMode === "receive" ? 1 : -1)
    setMode(nextMode)
    setAnimationKey((key) => key + 1)
  }

  return (
    <div
      ref={rootRef}
      className="flex w-full max-w-[min(85vw,26rem)] flex-col gap-3 rounded-2xl"
    >
      <div className="-outline-offset-1 relative grid grid-cols-2 rounded-2xl bg-gray-900 p-1 outline outline-white/5">
        <motion.div
          className="-outline-offset-1 absolute inset-y-1 rounded-xl bg-gray-800 outline outline-white/5"
          style={{ width: "calc((100% - 8px) / 2)" }}
          initial={false}
          animate={{
            left: mode === "send" ? "4px" : "50%",
          }}
          transition={{
            type: "spring",
            bounce: 0,
            duration: 0.2,
          }}
        />
        {(["send", "receive"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => selectMode(tab)}
            className="relative z-1 flex items-center justify-center rounded-xl py-3 text-center font-semibold text-base/5 text-white tracking-tight"
          >
            {modes[tab].label}
          </button>
        ))}
      </div>

      <div className="space-y-1 overflow-hidden rounded-2xl border border-white/5 bg-gray-900 p-1">
        <div className="relative overflow-hidden">
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.div
              key={mode}
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: PANEL_EASE }}
              className="space-y-1"
            >
              {mode === "send" ? <SendPanel /> : <ReceivePanel />}
            </motion.div>
          </AnimatePresence>
        </div>

        <StatusFooter
          key={animationKey}
          states={modes[mode].states}
          isPlaying={hasStarted}
        />
      </div>
    </div>
  )
}

export default ReceiveSendIllustration
